import { useEffect, useState } from "react";
import api, { runJob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, XCircle, Search, Loader2, Sparkles, Gauge } from "lucide-react";
import { toast } from "sonner";

export default function SeoToolkit() {
  const { user } = useAuth();
  const isStaff = user.role !== "client";
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(isStaff ? "" : user.client_id);
  const [op, setOp] = useState({ focus_keyword: "", title: "", meta_description: "", url_slug: "", content: "" });
  const [result, setResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [seed, setSeed] = useState("");
  const [kw, setKw] = useState(null);
  const [kwLoading, setKwLoading] = useState(false);

  useEffect(() => {
    if (isStaff) api.get("/clients").then((r) => { setClients(r.data); if (r.data[0]) setClientId(r.data[0].id); });
  }, [isStaff]);

  const analyze = async () => {
    if (!clientId) return;
    setAnalyzing(true);
    try { const { data } = await api.post(`/clients/${clientId}/seo/onpage`, op); setResult(data); }
    catch { toast.error("Analysis failed"); } finally { setAnalyzing(false); }
  };
  const research = async () => {
    if (!clientId) return;
    setKwLoading(true); setKw(null);
    try { const r = await runJob(api.post(`/clients/${clientId}/content/generate`, { type: "keywords", keywords: seed })); setKw(r.data); }
    catch (e) { toast.error(e.message); } finally { setKwLoading(false); }
  };
  const sc = result ? (result.score >= 80 ? "#10B981" : result.score >= 50 ? "#F59E0B" : "#EF4444") : "#94a3b8";
  const Group = ({ t, arr, c }) => arr?.length ? (
    <div><p className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">{t}</p>
      <div className="flex flex-wrap gap-1.5">{arr.map((k, i) => <span key={i} className={`text-[11px] px-2 py-0.5 rounded ${c}`}>{k}</span>)}</div></div>) : null;

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">SEO Toolkit</h1>
          <p className="text-slate-500 text-sm mt-1">On-page optimizer & keyword research — all in one</p>
        </div>
        {isStaff && (
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger className="w-48" data-testid="toolkit-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
            <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      <Tabs defaultValue="optimizer">
        <TabsList>
          <TabsTrigger value="optimizer" data-testid="tab-optimizer">Content Optimizer</TabsTrigger>
          <TabsTrigger value="keywords" data-testid="tab-keyword-research">Keyword Research</TabsTrigger>
        </TabsList>

        <TabsContent value="optimizer" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-5 border-slate-200 space-y-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">Focus keyword</Label>
                <Input value={op.focus_keyword} onChange={(e) => setOp({ ...op, focus_keyword: e.target.value })} className="mt-1.5" data-testid="op-keyword" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">SEO title</Label>
                <Input value={op.title} onChange={(e) => setOp({ ...op, title: e.target.value })} className="mt-1.5" data-testid="op-title" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">Meta description</Label>
                <Textarea value={op.meta_description} onChange={(e) => setOp({ ...op, meta_description: e.target.value })} rows={2} className="mt-1.5" data-testid="op-meta" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">URL slug</Label>
                <Input value={op.url_slug} onChange={(e) => setOp({ ...op, url_slug: e.target.value })} className="mt-1.5" data-testid="op-slug" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">Content</Label>
                <Textarea value={op.content} onChange={(e) => setOp({ ...op, content: e.target.value })} rows={8} className="mt-1.5" placeholder="Paste your page/blog content…" data-testid="op-content" /></div>
              <Button onClick={analyze} disabled={analyzing || !clientId} className="text-white w-full" style={{ background: "var(--tenant-primary)" }} data-testid="op-analyze-btn">
                {analyzing ? <Loader2 size={16} className="animate-spin" /> : "Analyze content"}
              </Button>
            </Card>

            <Card className="p-5 border-slate-200">
              {!result ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-10 text-slate-400">
                  <Gauge size={32} className="mb-2" /><p className="text-sm">Run an analysis to see your SEO score & checklist.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-4 mb-5">
                    <div className="h-20 w-20 rounded-full flex items-center justify-center font-display text-2xl font-bold text-white" style={{ background: sc }} data-testid="op-score">{result.score}</div>
                    <div>
                      <p className="font-display font-semibold text-slate-900">SEO Score</p>
                      <p className="text-sm text-slate-500">{result.passed}/{result.total} checks passed · {result.word_count} words</p>
                      <p className="text-xs text-slate-400">Readability: {result.readability} · density {result.keyword_density}%</p>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-80 overflow-y-auto scroll-thin">
                    {result.checks.map((c, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm" data-testid="op-check">
                        {c.ok ? <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 shrink-0" /> : <XCircle size={16} className="text-red-400 mt-0.5 shrink-0" />}
                        <div><span className={c.ok ? "text-slate-700" : "text-slate-800"}>{c.label}</span>
                          {!c.ok && <p className="text-[12px] text-slate-400">{c.advice}</p>}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="keywords" className="mt-4">
          <Card className="p-5 border-slate-200">
            <div className="flex gap-2 mb-4">
              <Input value={seed} onChange={(e) => setSeed(e.target.value)} placeholder="Seed keyword or topic (optional)…" data-testid="kw-seed" />
              <Button onClick={research} disabled={kwLoading || !clientId} className="text-white shrink-0" style={{ background: "var(--tenant-primary)" }} data-testid="kw-research-btn">
                {kwLoading ? <Loader2 size={16} className="animate-spin" /> : <><Search size={15} className="mr-1.5" /> Research</>}
              </Button>
            </div>
            {kwLoading && <p className="text-sm text-slate-400">Researching local keyword opportunities…</p>}
            {kw && (
              <div className="space-y-4" data-testid="kw-results">
                <Group t="Primary keywords" arr={kw.primary} c="bg-orange-50 text-orange-700" />
                <Group t="Local keywords" arr={kw.local} c="bg-emerald-50 text-emerald-700" />
                <Group t="Long-tail" arr={kw.long_tail} c="bg-blue-50 text-blue-700" />
                <Group t="Competitor-gap keywords" arr={kw.competitor_gap} c="bg-violet-50 text-violet-700" />
                <Group t="Questions people ask" arr={kw.questions} c="bg-slate-100 text-slate-600" />
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
